import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BlockIcon from '@mui/icons-material/Block';
import EmailIcon from '@mui/icons-material/Email';
import GroupIcon from '@mui/icons-material/Group';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import RefreshIcon from '@mui/icons-material/Refresh';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { Link as RouterLink, useOutletContext, useParams } from 'react-router-dom';
import {
  disableClubPortal,
  disableManagedClub,
  disableSecretaryPortal,
  generateClubPortalPin,
  generateManagedClubPin,
  generateSecretaryPortalPin,
  getClubPortalPin,
  getManagedClubPin,
  getPortalAccess,
  getSecretaryManagedAccess,
  getSecretaryPortalPin,
  portalErrorMessage,
  sendClubPortalEmail,
  sendManagedClubEmail,
  sendSecretaryPortalEmail,
  whatsappClubPortalContact,
  whatsappManagedClubContact,
  whatsappSecretaryPortalContact
} from '../../features/portal/portalService';
import {
  clubPortalBase,
  formatPortalDate,
  portalUrl,
  secretaryPortalBase
} from './portalPaths';
import { PortalError, PortalLoading } from './portalCommon';
import PortalAccessDialog from './portalAccessDialog';
import PortalBulkEmailDialog from './portalBulkEmailDialog';
import PortalContactsDialog from './portalContactsDialog';

const scopeKey = (scope) =>
  scope?.kind === 'secretary' ? 'secretary' : `club:${scope?.venueId}`;

const scopeLabel = (scope) =>
  scope?.kind === 'secretary' ? 'Tournament Secretary' : scope?.venueName || 'Club';

const PinStatus = ({ hasPin }) => (
  <Chip
    size="small"
    color={hasPin ? 'success' : 'default'}
    label={hasPin ? 'PIN set' : 'No PIN'}
  />
);

const eventCountLabel = (value) => {
  const count = Number(value) || 1;
  return `${count} ${count === 1 ? 'event' : 'events'}`;
};

const CoverageChip = ({ shared, eventCount }) => (
  <Chip
    size="small"
    variant="outlined"
    color={shared ? 'primary' : 'default'}
    label={shared ? `Shared · ${eventCountLabel(eventCount)}` : 'This event only'}
  />
);

const PERSONAL_PIN_REASONS = {
  setting_off: 'Personal PINs are switched off in system settings.',
  not_shared: 'Personal PINs need a shared portal. Create a shared PIN first.',
  no_pin: 'Generate a PIN for this portal first.',
  no_saved_contacts: 'Add a saved contact for this portal first.'
};

const PersonalPinChip = ({ available, reason }) => {
  if (typeof available !== 'boolean') return null;

  return (
    <Tooltip
      title={
        available
          ? 'Saved contacts can receive their own PIN.'
          : PERSONAL_PIN_REASONS[reason] ||
            'Personal PINs are not available for this portal.'
      }>
      <Chip
        size="small"
        variant="outlined"
        color={available ? 'success' : 'default'}
        label={available ? 'Personal PINs' : 'Personal PINs off'}
      />
    </Tooltip>
  );
};

const PinMeta = ({ pinUpdatedAt, lastLoginAt, contactEmail, contactCount }) => {
  const updated = formatPortalDate(pinUpdatedAt);
  const lastLogin = formatPortalDate(lastLoginAt);
  const contacts = Number(contactCount) || 0;

  return (
    <>
      <Typography variant="caption" color="text.secondary" display="block">
        {updated ? `PIN updated ${updated}` : 'No PIN generated yet'}
        {lastLogin ? ` • Last sign-in ${lastLogin}` : ''}
        {` • ${contacts} ${contacts === 1 ? 'contact' : 'contacts'}`}
      </Typography>
      {contactEmail && (
        <Typography variant="caption" color="text.secondary" display="block">
          Email: {contactEmail}
        </Typography>
      )}
    </>
  );
};

const PortalAdmin = ({ mode = 'admin' }) => {
  const { eventId } = useParams();
  const outlet = useOutletContext();
  const isPortal = mode === 'portal';
  const token = isPortal ? outlet?.token || '' : '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionWarning, setActionWarning] = useState('');
  const [actionKey, setActionKey] = useState('');
  const [reload, setReload] = useState(0);
  const [dialog, setDialog] = useState(null);
  const [pinLoadingKey, setPinLoadingKey] = useState('');
  const pinRequestRef = useRef(0);
  const [disableTarget, setDisableTarget] = useState(null);
  const [contactsScope, setContactsScope] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (isPortal && !token) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setLoadError('');

    const request = isPortal ? getSecretaryManagedAccess(token) : getPortalAccess(eventId);

    request
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((requestError) => {
        if (!cancelled) {
          setLoadError(portalErrorMessage(requestError, 'The portal settings could not be loaded.'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [eventId, isPortal, reload, token]);

  const refresh = useCallback(() => setReload((value) => value + 1), []);

  const openDetails = async (scope) => {
    setActionError('');
    setActionWarning('');

    const key = scopeKey(scope);
    const requestId = pinRequestRef.current + 1;
    pinRequestRef.current = requestId;

    setDialog({
      ...scope,
      pin: null,
      pinSet: Boolean(scope.hasPin),
      pinExisting: false,
      pinRecoverable: null
    });

    if (!scope.hasPin) {
      setPinLoadingKey('');
      return;
    }

    setPinLoadingKey(key);

    try {
      let result;
      if (scope.kind === 'secretary') {
        result = await getSecretaryPortalPin(eventId);
      } else if (isPortal) {
        result = await getManagedClubPin(token, scope.venueId);
      } else {
        result = await getClubPortalPin(eventId, scope.venueId);
      }

      if (pinRequestRef.current !== requestId) return;

      setDialog((current) => {
        if (!current || scopeKey(current) !== key) return current;
        return {
          ...current,
          pin: result?.pin || null,
          pinExisting: Boolean(result?.pin),
          pinRecoverable: Boolean(result?.recoverable),
          pinUpdatedAt: result?.pinUpdatedAt || current.pinUpdatedAt
        };
      });
    } catch {
      if (pinRequestRef.current !== requestId) return;

      setDialog((current) =>
        current && scopeKey(current) === key
          ? { ...current, pin: null, pinExisting: false, pinRecoverable: null }
          : current
      );
    } finally {
      if (pinRequestRef.current === requestId) setPinLoadingKey('');
    }
  };

  const handleGenerate = async (scope) => {
    if (!scope || (isPortal && scope.kind === 'secretary')) return;

    pinRequestRef.current += 1;
    setPinLoadingKey('');
    const key = scopeKey(scope);
    setActionKey(key);
    setActionError('');
    setActionWarning('');

    try {
      let result;

      if (scope.kind === 'secretary') {
        result = await generateSecretaryPortalPin(eventId);
      } else if (isPortal) {
        result = await generateManagedClubPin(token, scope.venueId);
      } else {
        result = await generateClubPortalPin(eventId, scope.venueId);
      }

      if (result.cleanupWarning) setActionWarning(result.cleanupWarning);

      setDialog({
        ...scope,
        venueName: result.venueName || scope.venueName,
        pin: result.pin || null,
        pinSet: true,
        pinExisting: false,
        pinRecoverable: Boolean(result.pinRecoverable),
        pinUpdatedAt: result.pinUpdatedAt,
        contactEmail: result.contactEmail || scope.contactEmail || '',
        shared: result.shared !== undefined ? result.shared : scope.shared,
        pinScope: result.shared ? 'workspace' : scope.pinScope,
        eventCount: result.eventCount || scope.eventCount || 1
      });
      refresh();
    } catch (requestError) {
      setActionError(
        portalErrorMessage(requestError, 'The PIN could not be generated. Please try again.')
      );
    } finally {
      setActionKey('');
    }
  };

  const handleDisable = async () => {
    if (!disableTarget || (isPortal && disableTarget.kind === 'secretary')) return;

    const key = scopeKey(disableTarget);
    setActionKey(key);
    setActionError('');
    setActionWarning('');

    try {
      let result;
      if (disableTarget.kind === 'secretary') {
        result = await disableSecretaryPortal(eventId);
      } else if (isPortal) {
        result = await disableManagedClub(token, disableTarget.venueId);
      } else {
        result = await disableClubPortal(eventId, disableTarget.venueId);
      }

      if (result?.cleanupWarning) setActionWarning(result.cleanupWarning);
      setDisableTarget(null);
      setDialog((current) => (current && scopeKey(current) === key ? null : current));
      refresh();
    } catch (requestError) {
      setActionError(
        portalErrorMessage(requestError, 'The portal access could not be disabled.')
      );
    } finally {
      setActionKey('');
    }
  };

  const handleSendEmail = async (email, contactName = '') => {
    if (!dialog) return null;

    const basePath =
      dialog.kind === 'secretary'
        ? secretaryPortalBase(eventId)
        : clubPortalBase(eventId, dialog.venueId);

    const payload = {
      email,
      url: portalUrl(basePath),
      ...(contactName ? { contactName } : {}),
      ...(dialog.pin ? { pin: dialog.pin } : {})
    };

    let result;

    if (dialog.kind === 'secretary') {
      result = await sendSecretaryPortalEmail(eventId, payload);
    } else if (isPortal) {
      result = await sendManagedClubEmail(token, dialog.venueId, payload);
    } else {
      result = await sendClubPortalEmail(eventId, dialog.venueId, payload);
    }

    setDialog((current) =>
      current
        ? {
            ...current,
            contactEmail: result?.email || email,
            shared: result?.shared !== undefined ? result.shared : current.shared,
            eventCount: result?.eventCount || current.eventCount
          }
        : current
    );
    refresh();

    return result;
  };

  const handleSendWhatsApp = async (contact) => {
    if (!dialog || !contact?.id) return null;

    const basePath =
      dialog.kind === 'secretary'
        ? secretaryPortalBase(eventId)
        : clubPortalBase(eventId, dialog.venueId);
    const payload = { url: portalUrl(basePath) };

    let result;
    if (dialog.kind === 'secretary') {
      result = await whatsappSecretaryPortalContact(eventId, contact.id, payload);
    } else if (isPortal) {
      result = await whatsappManagedClubContact(token, dialog.venueId, contact.id, payload);
    } else {
      result = await whatsappClubPortalContact(eventId, dialog.venueId, contact.id, payload);
    }

    refresh();
    return result;
  };

  const clubScope = (club) => ({
    kind: 'club',
    venueId: club.venueId,
    venueName: club.venueName,
    hasPin: club.hasPin,
    pinUpdatedAt: club.pinUpdatedAt,
    lastLoginAt: club.lastLoginAt,
    contactEmail: club.contactEmail || '',
    contactCount: Number(club.contactCount) || 0,
    shared: Boolean(club.shared),
    pinScope: club.pinScope,
    eventCount: club.eventCount || 1
  });

  const secretaryScope = () => ({
    kind: 'secretary',
    venueId: null,
    venueName: 'Tournament Secretary',
    hasPin: data?.secretary?.hasPin,
    pinUpdatedAt: data?.secretary?.pinUpdatedAt,
    lastLoginAt: data?.secretary?.lastLoginAt,
    contactEmail: data?.secretary?.contactEmail || '',
    contactCount: Number(data?.secretary?.contactCount) || 0,
    shared: Boolean(data?.secretary?.shared),
    pinScope: data?.secretary?.pinScope,
    eventCount: data?.secretary?.eventCount || 1
  });

  const renderActions = (scope) => {
    const busy = actionKey === scopeKey(scope);
    const hasPin = Boolean(scope.hasPin);
    let label = 'Generate PIN';
    if (scope.shared && hasPin) label = 'Regenerate PIN';
    if (!scope.shared) {
      label =
        scope.kind === 'secretary'
          ? 'Create workspace-wide PIN'
          : 'Create club-wide PIN';
    }

    return (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={2}>
        <Button
          variant="contained"
          size="small"
          startIcon={
            busy ? <CircularProgress size={16} color="inherit" /> : <VpnKeyIcon />
          }
          disabled={busy}
          onClick={() => handleGenerate(scope)}>
          {busy ? 'Working…' : label}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<QrCode2Icon />}
          onClick={() => openDetails(scope)}>
          Link &amp; QR
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<GroupIcon />}
          onClick={() => {
            setActionError('');
            setActionWarning('');
            setContactsScope(scope);
          }}>
          Contacts ({scope.contactCount || 0})
        </Button>
        {hasPin && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<BlockIcon />}
            disabled={busy}
            onClick={() => {
              setActionError('');
              setDisableTarget(scope);
            }}>
            Disable
          </Button>
        )}
      </Stack>
    );
  };

  const clubs = data?.clubs || [];
  const contactLimits = data?.contactLimits || {
    perPortal: 20,
    bulkPortals: 20,
    bulkRecipients: 100
  };
  const bulkPortals = data
    ? [
        ...(data.secretary
          ? [
              {
                kind: 'secretary',
                venueId: null,
                venueName: 'Tournament Secretary',
                contactCount: Number(data.secretary.contactCount) || 0,
                hasPin: Boolean(data.secretary.hasPin),
                shared: Boolean(data.secretary.shared),
                personalAvailable: data.secretary.personalAvailable,
                personalUnavailableReason: data.secretary.personalUnavailableReason
              }
            ]
          : []),
        ...clubs.map((club) => ({
          kind: 'club',
          venueId: club.venueId,
          venueName: club.venueName,
          contactCount: Number(club.contactCount) || 0,
          hasPin: Boolean(club.hasPin),
          shared: Boolean(club.shared),
          personalAvailable: club.personalAvailable,
          personalUnavailableReason: club.personalUnavailableReason
        }))
      ]
    : [];
  const canBulkEmail = bulkPortals.some((portal) => portal.contactCount > 0);
  const dialogUrl = dialog
    ? portalUrl(
        dialog.kind === 'secretary'
          ? secretaryPortalBase(eventId)
          : clubPortalBase(eventId, dialog.venueId)
      )
    : '';
  const disableBusy = disableTarget && actionKey === scopeKey(disableTarget);
  const noWorkspaceScope = data?.scope?.workspaceScope === 'none';
  const sharedScopeHelp = isPortal
    ? 'Each club PIN covers every event at that club in this workspace. Officials use the event picker in the portal to switch events without signing in again.'
    : 'Each club PIN covers every event at that club in this workspace, and the secretary PIN covers every event in the workspace. Officials use the event picker to switch events without signing in again.';
  let secretaryCoverageCopy = 'Can update every game at every club in this event.';
  if (data?.secretary?.shared) {
    secretaryCoverageCopy = 'Can update every game at every club in every event in this workspace.';
    if (noWorkspaceScope) {
      secretaryCoverageCopy =
        'Can update every game at every club in every no-workspace event.';
    }
  }
  let disableCopy = 'This portal will stop working immediately.';
  if (disableTarget?.shared) {
    disableCopy = `This shared PIN will stop working at every event in its scope (${eventCountLabel(disableTarget.eventCount)}). Every device signed in to it will be signed out, and the PIN can no longer be used. You can issue a new PIN later.`;
  } else if (disableTarget) {
    disableCopy = `${scopeLabel(disableTarget)} will stop working immediately. Every device signed in to it will be signed out, and its PIN can no longer be used. You can issue a new PIN later.`;
  }

  return (
    <Box
      minHeight={isPortal ? undefined : '100vh'}
      bgcolor={isPortal ? undefined : 'background.default'}
      py={isPortal ? 0 : { xs: 2, sm: 4 }}>
      <Container maxWidth="md" disableGutters={isPortal}>
        <Button
          component={RouterLink}
          to={
            isPortal
              ? secretaryPortalBase(eventId)
              : `/events/${encodeURIComponent(eventId)}`
          }
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 1, ml: isPortal ? 0 : -1 }}>
          {isPortal ? 'Back to clubs' : 'Back to event'}
        </Button>

        <Box
          display="flex"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          gap={1}
          mb={3}
          flexWrap="wrap"
          useFlexGap>
          <Box flex={1} minWidth={0}>
            <Typography variant={isPortal ? 'h5' : 'h4'} fontWeight="bold">
              {isPortal ? 'Manage club portals' : 'Event Portals'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {data?.event?.name || 'Issue PINs for scoring without a BowlScout account'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              size="small"
              startIcon={<EmailIcon />}
              disabled={!data || !canBulkEmail}
              onClick={() => {
                setActionError('');
                setActionWarning('');
                setBulkOpen(true);
              }}
              sx={{ whiteSpace: 'nowrap' }}>
              {isPortal ? 'Email club portals' : 'Email portals'}
            </Button>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={refresh} disabled={loading && !data}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        {loading && !data && <PortalLoading />}
        {loadError && (
          <Box mb={2}>
            <PortalError message={loadError} onRetry={refresh} />
          </Box>
        )}
        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>
            {actionError}
          </Alert>
        )}
        {actionWarning && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setActionWarning('')}>
            {actionWarning}
          </Alert>
        )}

        {data && (
          <>
            {data.personalLoginEnabled === false && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Personal PINs are disabled</AlertTitle>
                New portal emails and WhatsApp shares will send the shared PIN; personal PINs
                already issued keep working. A superuser can enable personal PINs in Settings.
              </Alert>
            )}

            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, mb: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                How the portals work
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isPortal
                  ? 'Generate or replace a PIN for any club. The PIN works at every event at that club in this workspace, and the technical official at that venue can open the link on a phone or desktop and update any game played there. You can also email the link and PIN to the contact stored for that club. Devices stay signed in until the official logs out, or you regenerate or disable the PIN.'
                  : 'Each club gets a link and a 5-digit PIN that works across events at the same club in this workspace. The technical official at that venue can open the link on a phone or desktop and update any game played there; after signing in, they can switch between the covered events. The tournament secretary PIN covers every event in the workspace and can manage club portals. Devices stay signed in until the official logs out, or you regenerate or disable the PIN.'}
              </Typography>
            </Paper>

            <Alert severity={noWorkspaceScope ? 'warning' : 'info'} sx={{ mb: 3 }}>
              <AlertTitle>
                {noWorkspaceScope
                  ? 'This event is not in a workspace'
                  : 'One PIN, multiple events'}
              </AlertTitle>
              {noWorkspaceScope
                ? 'Shared club PINs match the same club ID across every no-workspace event, and a shared secretary PIN would cover every no-workspace event in BowlScout. Only superusers manage no-workspace events. Generate shared PINs with care.'
                : sharedScopeHelp}
            </Alert>

            {data.unassignedGameCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {data.unassignedGameCount}{' '}
                {data.unassignedGameCount === 1 ? 'game has' : 'games have'} no club or venue.
                Those games can only be updated through the tournament secretary portal.
              </Alert>
            )}

            {!isPortal && (
              <Card variant="outlined" sx={{ borderColor: 'primary.main', borderRadius: 2 }}>
                <CardContent>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                    spacing={1}>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        Tournament Secretary
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {secretaryCoverageCopy}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <PinStatus hasPin={data.secretary?.hasPin} />
                      <CoverageChip
                        shared={data.secretary?.shared}
                        eventCount={data.secretary?.eventCount}
                      />
                      {data.personalLoginEnabled === true && (
                        <PersonalPinChip
                          available={data.secretary?.personalAvailable}
                          reason={data.secretary?.personalUnavailableReason}
                        />
                      )}
                    </Stack>
                  </Stack>

                  <Box mt={1}>
                    <PinMeta
                      pinUpdatedAt={data.secretary?.pinUpdatedAt}
                      lastLoginAt={data.secretary?.lastLoginAt}
                      contactEmail={data.secretary?.contactEmail}
                      contactCount={data.secretary?.contactCount}
                    />
                  </Box>

                  {renderActions(secretaryScope())}
                </CardContent>
              </Card>
            )}

            <Typography variant="h6" fontWeight="bold" mt={isPortal ? 2 : 4} mb={1}>
              Club portals
            </Typography>

            {clubs.length === 0 ? (
              <Alert severity="info">
                This event has no venue locations yet, so there are no club portals to issue. Add
                the club venues to the event first. The tournament secretary portal can already
                update all games.
              </Alert>
            ) : (
              <Box
                display="grid"
                gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                gap={2}>
                {clubs.map((club) => {
                  const scope = clubScope(club);
                  return (
                    <Card key={club.venueId} variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Stack
                          direction="row"
                          alignItems="flex-start"
                          justifyContent="space-between"
                          spacing={1}>
                          <Box minWidth={0}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {club.venueName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {club.gameCount} {club.gameCount === 1 ? 'game' : 'games'} in{' '}
                              {club.sectionCount}{' '}
                              {club.sectionCount === 1 ? 'section' : 'sections'}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <PinStatus hasPin={club.hasPin} />
                            <CoverageChip shared={club.shared} eventCount={club.eventCount} />
                            {data.personalLoginEnabled === true && (
                              <PersonalPinChip
                                available={club.personalAvailable}
                                reason={club.personalUnavailableReason}
                              />
                            )}
                          </Stack>
                        </Stack>

                        <Box mt={1}>
                          <PinMeta
                            pinUpdatedAt={club.pinUpdatedAt}
                            lastLoginAt={club.lastLoginAt}
                            contactEmail={club.contactEmail}
                            contactCount={club.contactCount}
                          />
                          {club.shared && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block">
                              {noWorkspaceScope
                                ? `Covers ${eventCountLabel(club.eventCount)} that are not in a workspace.`
                                : `Covers ${eventCountLabel(club.eventCount)} at this club in this workspace.`}
                            </Typography>
                          )}
                        </Box>

                        {renderActions(scope)}
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}

            <Typography variant="caption" color="text.secondary" display="block" mt={3}>
              Share each PIN only with the official responsible for that club. Creating a shared
              PIN replaces the event-only PINs at other events in its scope, and regenerating or
              disabling a PIN signs out every device already using it.
            </Typography>
          </>
        )}
      </Container>

      <PortalAccessDialog
        open={Boolean(dialog)}
        onClose={() => {
          pinRequestRef.current += 1;
          setPinLoadingKey('');
          setDialog(null);
          setActionError('');
        }}
        mode={mode}
        token={token}
        eventId={eventId}
        eventName={data?.event?.name || ''}
        scopeLabel={dialog ? scopeLabel(dialog) : 'Portal'}
        url={dialogUrl}
        pin={dialog?.pin || ''}
        pinSet={Boolean(dialog?.pinSet)}
        pinExisting={Boolean(dialog?.pinExisting)}
        pinRecoverable={dialog?.pinRecoverable ?? null}
        pinLoading={Boolean(dialog && pinLoadingKey === scopeKey(dialog))}
        pinUpdatedAt={dialog?.pinUpdatedAt}
        email={dialog?.contactEmail || ''}
        shared={Boolean(dialog?.shared)}
        eventCount={dialog?.eventCount || 1}
        scopeKind={dialog?.kind || 'club'}
        venueId={dialog?.venueId || null}
        workspaceScope={data?.scope?.workspaceScope || null}
        busy={Boolean(dialog && actionKey === scopeKey(dialog))}
        error={actionError}
        onRegenerate={() => handleGenerate(dialog)}
        onSendEmail={handleSendEmail}
        onSendWhatsApp={handleSendWhatsApp}
      />

      <Dialog
        open={Boolean(disableTarget)}
        onClose={disableBusy ? undefined : () => setDisableTarget(null)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>
          {disableTarget?.shared ? 'Disable this shared PIN?' : 'Disable this portal?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{disableCopy}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableTarget(null)} disabled={disableBusy}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDisable} disabled={disableBusy}>
            {disableBusy ? <CircularProgress size={18} color="inherit" /> : 'Disable portal'}
          </Button>
        </DialogActions>
      </Dialog>

      <PortalContactsDialog
        open={Boolean(contactsScope)}
        onClose={() => setContactsScope(null)}
        mode={mode}
        token={token}
        eventId={eventId}
        kind={contactsScope?.kind || 'club'}
        venueId={contactsScope?.venueId || null}
        venueName={contactsScope?.venueName || ''}
        workspaceScope={data?.scope?.workspaceScope || null}
        contactLimit={contactLimits.perPortal}
        onChanged={refresh}
      />

      <PortalBulkEmailDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        mode={mode}
        token={token}
        eventId={eventId}
        portals={bulkPortals}
        limits={contactLimits}
        onDone={refresh}
      />
    </Box>
  );
};

export default PortalAdmin;
